import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'https://1v6zm22cmf.execute-api.us-west-2.amazonaws.com/prod';

const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export interface ChatResponse {
  message: string;
  conversationId: string;
  timestamp: string;
}

export interface AuthResponse {
  message: string;
  token: string;
  user: {
    userId: string;
    email: string;
    name: string;
  };
}

export const sendChatMessage = async (message: string, conversationId?: string, caseId?: string): Promise<ChatResponse> => {
  try {
    const response = await apiClient.post<ChatResponse>('/chat', {
      message,
      conversationId,
      caseId,
    });
    return response.data;
  } catch (error) {
    console.error('Error sending chat message:', error);
    throw error;
  }
};

export const signup = async (email: string, password: string, name: string): Promise<AuthResponse> => {
  try {
    const response = await apiClient.post<AuthResponse>('/auth/signup', {
      email,
      password,
      name,
    });
    return response.data;
  } catch (error: any) {
    console.error('Error during signup:', error);
    throw new Error(error.response?.data?.error || 'Signup failed');
  }
};

export const login = async (email: string, password: string): Promise<AuthResponse> => {
  try {
    const response = await apiClient.post<AuthResponse>('/auth/login', {
      email,
      password,
    });
    return response.data;
  } catch (error: any) {
    console.error('Error during login:', error);
    throw new Error(error.response?.data?.error || 'Login failed');
  }
};

export const checkHealth = async (): Promise<{ status: string }> => {
  try {
    const response = await apiClient.get('/health');
    return response.data;
  } catch (error) {
    console.error('Error checking health:', error);
    throw error;
  }
};

export interface UserProfile {
  userId: string;
  name: string;
  email: string;
  password: string;
  createdAt: string;
}

export interface UpdateProfileRequest {
  name?: string;
  email?: string;
  password?: string;
}

export const getUserProfile = async (): Promise<UserProfile> => {
  try {
    const token = localStorage.getItem('token');
    const response = await apiClient.get<UserProfile>('/auth/profile', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching user profile:', error);
    throw error;
  }
};

export const updateUserProfile = async (updates: UpdateProfileRequest): Promise<AuthResponse> => {
  try {
    const token = localStorage.getItem('token');
    const response = await apiClient.put<AuthResponse>('/auth/profile', updates, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error updating user profile:', error);
    throw error;
  }
};

export interface UploadResponse {
  uploadUrl: string;
  fileKey: string;
  message: string;
}

export const getUploadUrl = async (fileName: string, fileType: string): Promise<UploadResponse> => {
  try {
    const response = await apiClient.post<UploadResponse>('/upload', {
      fileName,
      fileType,
    });
    return response.data;
  } catch (error) {
    console.error('Error getting upload URL:', error);
    throw error;
  }
};

export const uploadFileToS3 = async (file: File): Promise<string> => {
  try {
    // Get presigned URL
    const { uploadUrl, fileKey } = await getUploadUrl(file.name, file.type);
    
    // Upload file to S3 using presigned URL
    await axios.put(uploadUrl, file, {
      headers: {
        'Content-Type': file.type,
      },
    });
    
    return fileKey;
  } catch (error) {
    console.error('Error uploading file:', error);
    throw error;
  }
};

// Conversation history interfaces and functions
export interface ConversationMessage {
  conversationId: string;
  userMessage: string;
  botResponse: string;
  timestamp: string;
  messageTimestamp: number;
}

export interface ConversationSummary {
  conversationId: string;
  lastMessage: string;
  lastResponse: string;
  lastTimestamp: string;
  messageCount: number;
}

export const getConversationHistory = async (conversationId: string): Promise<ConversationMessage[]> => {
  try {
    const response = await apiClient.get<{ conversationId: string; messages: ConversationMessage[] }>(
      `/conversations?conversationId=${conversationId}`
    );
    return response.data.messages;
  } catch (error) {
    console.error('Error fetching conversation history:', error);
    throw error;
  }
};

export const listConversations = async (): Promise<ConversationSummary[]> => {
  try {
    const response = await apiClient.get<{ conversations: ConversationSummary[] }>('/conversations');
    return response.data.conversations;
  } catch (error) {
    console.error('Error listing conversations:', error);
    throw error;
  }
};

// Case Management API - uses same API as main app
const caseApiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to case management requests
caseApiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export interface Case {
  caseId: string;
  caseNumber?: string;
  projectName: string;
  clientName?: string;
  clientId?: string;
  status: string;
  phase?: string;
  incidentDate?: string;
  createDate?: string;
  createdAt?: string;
  lastActivity?: string;
  lastModified?: string;
  openedDate?: string;
  closedDate?: string;
  dueDate?: string;
  description?: string;
  assignedTeam?: string;
  createdBy?: string;
  projectManager?: string;
  noteCount: number;
  docCount: number;
}

export interface CaseNote {
  noteId: string;
  authorId?: string;
  authorName: string;
  authorEmail?: string;
  body: string;
  plainText?: string;
  createdAt: string;
  modifiedAt?: string;
  typeTag?: string;
}

export interface CaseDocument {
  docId?: string;
  documentId?: string;
  fileName: string;
  title?: string;
  description?: string;
  s3Key?: string;
  s3Bucket?: string;
  uploadDate?: string;
  uploadedAt?: string;
  uploadedBy?: string;
  uploaderName?: string;
  fileType?: string;
  mimeType?: string;
  fileSize: number;
  createdAt?: string;
  modifiedAt?: string;
}

export interface CaseContact {
  contactId: string;
  personId?: string;
  name: string;
  firstName?: string;
  lastName?: string;
  role: string;
  emails?: string[];
  phones?: string[];
  addresses?: string[];
  isClient?: boolean;
  isPlaintiff?: boolean;
  isDefendant?: boolean;
  createdAt?: string;
}

export interface TeamMember {
  userId: string;
  userName: string;
  userEmail: string;
  accessLevel?: string;
  createdAt?: string;
}

export interface Team {
  teamId: string;
  teamName: string;
  description?: string;
  members: TeamMember[];
  memberCount: number;
  createdAt?: string;
  modifiedAt?: string;
}

export interface CaseTag {
  tagId: string;
  name: string;
  color?: string;
}

export interface CaseDetail extends Case {
  notes: CaseNote[];
  documents: CaseDocument[];
  contacts: CaseContact[];
  teams?: Team[];
  tags?: CaseTag[];
  stats: {
    noteCount: number;
    docCount: number;
    contactCount: number;
    teamCount?: number;
    tagCount?: number;
  };
}

export interface TimelineEvent {
  timestamp: string;
  eventType: string;
  description: string;
  actorName: string;
  actorId?: string;
  relatedId?: string;
  metadata?: any;
}

export const listCases = async (params?: { limit?: number; status?: string; offset?: number }): Promise<{ cases: Case[]; count: number; total: number; offset: number; hasMore: boolean }> => {
  try {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.status) queryParams.append('status', params.status);
    if (params?.offset) queryParams.append('offset', params.offset.toString());
    
    const response = await caseApiClient.get(`/cases?${queryParams.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Error listing cases:', error);
    throw error;
  }
};

export const getCaseDetail = async (caseId: string): Promise<CaseDetail> => {
  try {
    const response = await caseApiClient.get(`/cases/${caseId}`);
    const data = response.data;
    
    // Normalize document field names (backend uses documentId, frontend uses docId)
    const normalizedDocuments = (data.documents || []).map((doc: any) => ({
      ...doc,
      docId: doc.docId || doc.documentId,
      uploadDate: doc.uploadDate || doc.uploadedAt,
      uploaderName: doc.uploaderName || doc.uploadedBy,
      fileType: doc.fileType || doc.mimeType,
    }));
    
    // Backend returns flat structure, transform to match expected CaseDetail format
    const caseDetail: CaseDetail = {
      ...data.case,
      // Map caseName to projectName for frontend compatibility
      projectName: data.case.caseName || data.case.projectName,
      // Include the separate arrays
      notes: data.notes || [],
      documents: normalizedDocuments,
      contacts: data.contacts || [],
      teams: data.teams || [],
      tags: data.tags || [],
      stats: data.counts || {
        noteCount: data.notes?.length || 0,
        docCount: data.documents?.length || 0,
        contactCount: data.contacts?.length || 0,
        teamCount: data.teams?.length || 0,
        tagCount: data.tags?.length || 0,
      },
    };
    
    return caseDetail;
  } catch (error) {
    console.error('Error fetching case detail:', error);
    throw error;
  }
};

export const searchCases = async (searchTerm: string, limit?: number): Promise<{ cases: Case[]; count: number }> => {
  try {
    const queryParams = new URLSearchParams();
    queryParams.append('q', searchTerm);
    if (limit) queryParams.append('limit', limit.toString());
    
    const response = await caseApiClient.get(`/cases/search?${queryParams.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Error searching cases:', error);
    throw error;
  }
};

export const getCaseTimeline = async (caseId: string): Promise<{ timeline: TimelineEvent[]; count: number }> => {
  try {
    const response = await caseApiClient.get(`/cases/${caseId}/timeline`);
    return response.data;
  } catch (error) {
    console.error('Error fetching case timeline:', error);
    throw error;
  }
};

export interface CreateCaseRequest {
  projectName: string;
  clientName: string;
  clientId?: string;
  status?: string;
  phase?: string;
  incidentDate?: string;
  description?: string;
  customProjectType?: string;
}

export interface CreateCaseResponse {
  message: string;
  case: {
    caseId: string;
    caseNumber: string;
    projectName: string;
    clientName: string;
    status: string;
    phase: string;
    createDate: string;
  };
}

export const createCase = async (caseData: CreateCaseRequest): Promise<CreateCaseResponse> => {
  try {
    const response = await caseApiClient.post('/cases', caseData);
    return response.data;
  } catch (error) {
    console.error('Error creating case:', error);
    throw error;
  }
};

export interface UpdateCaseRequest {
  projectName?: string;
  clientName?: string;
  status?: string;
  phase?: string;
  incidentDate?: string;
  description?: string;
  customProjectType?: string;
}

export const updateCase = async (caseId: string, caseData: UpdateCaseRequest): Promise<{ message: string; case: any }> => {
  try {
    const response = await caseApiClient.put(`/cases/${caseId}`, caseData);
    return response.data;
  } catch (error) {
    console.error('Error updating case:', error);
    throw error;
  }
};

export interface AddDocumentRequest {
  fileName: string;
  fileType: string;
  fileSize: number;
}

export interface AddDocumentResponse {
  message: string;
  uploadUrl: string;
  document: {
    docId: string;
    fileName: string;
    s3Key: string;
    uploadDate: string;
  };
}

export const addDocumentToCase = async (caseId: string, docData: AddDocumentRequest): Promise<AddDocumentResponse> => {
  try {
    const response = await caseApiClient.post(`/cases/${caseId}/documents`, docData);
    return response.data;
  } catch (error) {
    console.error('Error adding document to case:', error);
    throw error;
  }
};

export interface AddNoteRequest {
  body: string;
  typeTag?: string;
}

export interface AddNoteResponse {
  message: string;
  note: {
    noteId: string;
    authorName: string;
    body: string;
    createdAt: string;
    typeTag: string;
  };
}

export const addNoteToCase = async (caseId: string, noteData: AddNoteRequest): Promise<AddNoteResponse> => {
  try {
    const response = await caseApiClient.post(`/cases/${caseId}/notes`, noteData);
    return response.data;
  } catch (error) {
    console.error('Error adding note to case:', error);
    throw error;
  }
};

export interface AddContactRequest {
  fullName?: string;
  firstName?: string;
  lastName?: string;
  name?: string;
  role: string;
  email?: string;
  phone?: string;
  address?: string;
  isClient?: boolean;
}

export interface AddContactResponse {
  message: string;
  contactId: string;
  personId: string;
}

export const addContactToCase = async (caseId: string, contactData: AddContactRequest): Promise<AddContactResponse> => {
  try {
    const response = await caseApiClient.post(`/cases/${caseId}/contacts`, contactData);
    return response.data;
  } catch (error) {
    console.error('Error adding contact to case:', error);
    throw error;
  }
};

export interface DownloadDocumentResponse {
  downloadUrl: string;
  fileName: string;
  fileType: string;
  fileSize: number;
}

export const getDocumentDownloadUrl = async (caseId: string, docId: string, isPreview: boolean = false): Promise<DownloadDocumentResponse> => {
  try {
    // URL-encode the docId to handle special characters like #
    const encodedDocId = encodeURIComponent(docId);
    const previewParam = isPreview ? '?preview=true' : '';
    const response = await caseApiClient.get(`/cases/${caseId}/documents/${encodedDocId}/download${previewParam}`);
    return response.data;
  } catch (error) {
    console.error('Error getting document download URL:', error);
    throw error;
  }
};

// Billing API interfaces and functions
export interface BillingItem {
  billingId: string;
  date: string;
  description: string;
  hours: number;
  rate: number;
  amount: number;
  billingType: string;
  billable: boolean;
  attorney?: string;
  createdAt: string;
}

export interface BillingSummary {
  totalItems: number;
  totalBillableHours: number;
  totalNonBillableHours: number;
  totalBillableAmount: number;
  totalNonBillableAmount: number;
  totalInvoicedAmount: number;
  totalUnbilledAmount: number;
}

export interface MonthlyBillingCase {
  caseId: string;
  items: number;
  hours: number;
  amount: number;
  billableHours: number;
  billableAmount: number;
}

export interface MonthlyBillingReport {
  month: string;
  userId: string;
  totalItems: number;
  totalHours: number;
  totalAmount: number;
  totalBillableHours: number;
  totalBillableAmount: number;
  caseCount: number;
  cases: MonthlyBillingCase[];
}

export const getBillingSummary = async (caseId: string): Promise<BillingSummary> => {
  try {
    const response = await caseApiClient.get(`/billing/cases/${caseId}/summary`);
    return response.data.summary;
  } catch (error) {
    console.error('Error fetching billing summary:', error);
    throw error;
  }
};

export const getBillingItems = async (caseId: string, params?: { limit?: number; lastKey?: string }): Promise<{ items: BillingItem[]; hasMore: boolean; nextKey: string | null }> => {
  try {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.lastKey) queryParams.append('lastKey', params.lastKey);
    
    const response = await caseApiClient.get(`/billing/cases/${caseId}/items?${queryParams.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching billing items:', error);
    throw error;
  }
};

export const getMonthlyBillingReport = async (month: string): Promise<MonthlyBillingReport> => {
  try {
    const response = await caseApiClient.get(`/billing/monthly/${month}`);
    return response.data.report;
  } catch (error) {
    console.error('Error fetching monthly billing report:', error);
    throw error;
  }
};

// Teams API functions
export const getCaseTeams = async (caseId: string): Promise<{ teams: Team[] }> => {
  try {
    const response = await caseApiClient.get(`/cases/${caseId}/teams`);
    return response.data;
  } catch (error) {
    console.error('Error fetching case teams:', error);
    throw error;
  }
};

export default apiClient;
